import React from "react";

// Shared building blocks for full-page detail screens (inventory details, booking details):
// muted small labels over bold values, colour-coded sections, stat cards and status badges.

export const isEmpty = (v) => v === null || v === undefined || v === "";
export const show = (v) => (isEmpty(v) ? "-" : v);
export const titleCase = (v) => (isEmpty(v) ? "-" : String(v).replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()));

export const DETAIL_ACCENTS = {
  indigo: "#6366f1",
  sky: "#0ea5e9",
  violet: "#8b5cf6",
  teal: "#0ea5e9",
  green: "#10b981",
  amber: "#f59e0b",
  pink: "#ec4899",
  red: "#ef4444",
};

export const detailLabelStyle = {
  fontSize: 10.5, fontWeight: 600, letterSpacing: 0.6, textTransform: "uppercase", color: "#64748b", opacity: 0.75,
};

export const detailThStyle = {
  fontSize: 10.5, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.6px",
  background: "#f8fafc", borderBottom: "1px solid #e8edf5", padding: "10px 12px", whiteSpace: "nowrap",
};

export const detailTdStyle = { padding: "10px 12px", whiteSpace: "nowrap", color: "#1e293b", borderColor: "#f1f5f9" };

export const detailTableWrap = { border: "1px solid #eef1f7", borderRadius: 10 };

const GREEN = ["active", "approved", "verified", "completed", "created", "seats_added", "confirmed", "ticketed", "paid", "success", "yes"];
const AMBER = ["pending", "processing", "updated", "fare_updated", "initiated", "hold", "on_hold", "unpaid", "partial", "partially_paid", "submitted", "under_review", "in progress", "in_progress"];
const RED = ["inactive", "rejected", "failed", "deleted", "seats_removed", "cancelled", "canceled", "blocked", "no"];

export const badgeColors = (status) => {
  const s = String(status || "").toLowerCase();
  if (GREEN.includes(s)) return { bg: "#dcfce7", color: "#15803d", dot: "#22c55e" };
  if (AMBER.includes(s)) return { bg: "#fef3c7", color: "#b45309", dot: "#f59e0b" };
  if (RED.includes(s)) return { bg: "#fee2e2", color: "#b91c1c", dot: "#ef4444" };
  return { bg: "#f1f5f9", color: "#475569", dot: "#94a3b8" };
};

export const Badge = ({ value }) => {
  if (isEmpty(value)) return "-";
  const c = badgeColors(value);
  return (
    <span
      className="d-inline-flex align-items-center gap-1"
      style={{ background: c.bg, color: c.color, borderRadius: 20, padding: "3px 10px", fontSize: 11, fontWeight: 700, whiteSpace: "nowrap" }}
    >
      <span style={{ width: 6, height: 6, borderRadius: "50%", background: c.dot }}></span>
      {titleCase(value)}
    </span>
  );
};

export const IconBox = ({ icon, accent, size = 32 }) => (
  <span
    className="d-inline-flex align-items-center justify-content-center flex-shrink-0"
    style={{ width: size, height: size, borderRadius: 9, background: accent + "1a", color: accent, fontSize: size * 0.48 }}
  >
    <i className={"bi " + icon}></i>
  </span>
);

export const DetailSection = ({ icon, title, subtitle, accent, right, children }) => (
  <div
    style={{
      background: "#fff", borderRadius: 14, border: "1px solid #eef1f7", borderTop: `3px solid ${accent}`,
      boxShadow: "0 1px 3px rgba(15, 23, 42, 0.04)", padding: 20, marginBottom: 18,
    }}
  >
    <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
      <div className="d-flex align-items-center gap-2">
        <IconBox icon={icon} accent={accent} />
        <div>
          <div className="fw-bold" style={{ fontSize: 15, color: "#0f172a" }}>{title}</div>
          {subtitle && <div style={{ fontSize: 11.5, color: "#94a3b8" }}>{subtitle}</div>}
        </div>
      </div>
      {right}
    </div>
    {children}
  </div>
);

// items: [{ label, value, icon?, badge?, full? }]
export const DetailFields = ({ items, accent }) => (
  <div className="row g-3">
    {items.map((f, i) => (
      <div key={i} className={f.full ? "col-12" : "col-12 col-sm-6 col-lg-4"}>
        <div
          className="h-100 d-flex align-items-start gap-2"
          style={{ background: "#fafbfe", border: "1px solid #eef1f7", borderRadius: 10, padding: "10px 12px" }}
        >
          {f.icon && <i className={"bi " + f.icon} style={{ color: accent, fontSize: 14, marginTop: 1, opacity: 0.85 }}></i>}
          <div style={{ minWidth: 0 }}>
            <div style={{ ...detailLabelStyle, marginBottom: 3 }}>{f.label}</div>
            <div style={{ fontSize: 14, fontWeight: 600, color: isEmpty(f.value) ? "#cbd5e1" : "#0f172a", wordBreak: "break-word", whiteSpace: "pre-wrap" }}>
              {f.badge ? <Badge value={f.value} /> : show(f.value)}
            </div>
          </div>
        </div>
      </div>
    ))}
  </div>
);

export const StatCard = ({ icon, label, value, accent }) => (
  <div className="col-6 col-md-3">
    <div
      className="h-100 d-flex align-items-center gap-3"
      style={{ background: "#fff", border: "1px solid #eef1f7", borderRadius: 12, padding: "12px 14px", boxShadow: "0 1px 3px rgba(15, 23, 42, 0.04)" }}
    >
      <IconBox icon={icon} accent={accent} size={38} />
      <div style={{ minWidth: 0 }}>
        <div style={detailLabelStyle}>{label}</div>
        <div className="fw-bold text-truncate" style={{ fontSize: 18, color: "#0f172a", lineHeight: 1.2 }}>{value}</div>
      </div>
    </div>
  </div>
);

export const Pill = ({ value, accent }) => (
  <span style={{ background: accent + "1a", color: accent, borderRadius: 6, padding: "2px 8px", fontWeight: 700, fontSize: 12 }}>{value}</span>
);

export const Avatar = ({ name, accent }) => (
  <span
    className="d-inline-flex align-items-center justify-content-center fw-bold text-uppercase flex-shrink-0"
    style={{ width: 28, height: 28, borderRadius: "50%", background: accent + "1a", color: accent, fontSize: 12 }}
  >
    {(name || "?").trim().charAt(0) || "?"}
  </span>
);

export const EmptyState = ({ text }) => (
  <div className="text-center py-4" style={{ fontSize: 13, color: "#94a3b8" }}>
    <i className="bi bi-inbox d-block mb-1" style={{ fontSize: 22 }}></i>
    {text}
  </div>
);

export const HeroChip = ({ icon, children }) => (
  <span style={{ background: "rgba(255,255,255,.18)", borderRadius: 8, padding: "4px 10px", fontSize: 12, fontWeight: 700, whiteSpace: "nowrap" }}>
    {icon && <i className={"bi " + icon + " me-1"}></i>}
    {children}
  </span>
);

// Gradient header card for a person / company: logo (or icon), name, chips and contact meta.
// meta: [{ icon, text, href? }]
export const ProfileHero = ({ logo, icon = "bi-building", title, chips, right, action, meta = [] }) => (
  <div
    className="text-white mb-3"
    style={{
      background: "linear-gradient(135deg, var(--btn-bg) 0%, var(--btn-hover-bg) 100%)",
      borderRadius: 16, padding: "20px 24px", boxShadow: "0 8px 24px rgba(15, 23, 42, 0.12)", position: "relative", overflow: "hidden",
    }}
  >
    <i className={"bi " + icon} style={{ position: "absolute", right: -10, bottom: -40, fontSize: 160, opacity: 0.07 }}></i>
    <div className="d-flex align-items-center flex-wrap gap-3" style={{ position: "relative" }}>
      {logo ? (
        <img
          src={logo}
          alt=""
          style={{ width: 64, height: 64, borderRadius: 14, objectFit: "contain", background: "#fff", padding: 4, flexShrink: 0 }}
        />
      ) : (
        <span
          className="d-flex align-items-center justify-content-center flex-shrink-0"
          style={{ width: 64, height: 64, borderRadius: 14, background: "rgba(255,255,255,.2)", fontSize: 28 }}
        >
          <i className={"bi " + icon}></i>
        </span>
      )}
      <div className="flex-grow-1" style={{ minWidth: 0 }}>
        <div className="d-flex align-items-center flex-wrap gap-2">
          <span className="fw-bold" style={{ fontSize: 22, lineHeight: 1.2 }}>{show(title)}</span>
          {right}
        </div>
        {chips && <div className="d-flex align-items-center flex-wrap gap-2 mt-2">{chips}</div>}
        {meta.length > 0 && (
          <div className="d-flex flex-wrap gap-3 mt-2" style={{ fontSize: 12.5, opacity: 0.9 }}>
            {meta.filter((m) => !isEmpty(m.text)).map((m, i) => (
              <span key={i} className="d-inline-flex align-items-center gap-1">
                <i className={"bi " + m.icon}></i>
                {m.href ? <a href={m.href} style={{ color: "#fff", textDecoration: "none" }}>{m.text}</a> : m.text}
              </span>
            ))}
          </div>
        )}
      </div>
      {action && <div className="ms-md-auto flex-shrink-0">{action}</div>}
    </div>
  </div>
);

// Gradient header card with a FROM ---✈--- TO route line.
// from / to: { code, city, time, sub, align }
export const RouteHero = ({ chips, right, from, to, middle }) => {
  const side = (s, align) => (
    <div style={{ textAlign: align, minWidth: 90 }}>
      <div style={{ fontSize: 30, fontWeight: 800, letterSpacing: 1, lineHeight: 1 }}>{show(s?.code)}</div>
      <div style={{ fontSize: 12, opacity: 0.8, marginTop: 4 }}>{s?.city || " "}</div>
      <div style={{ fontSize: 17, fontWeight: 700, marginTop: 6 }}>{show(s?.time)}</div>
      {s?.sub && <div style={{ fontSize: 11, opacity: 0.75 }}>{s.sub}</div>}
    </div>
  );

  return (
    <div
      className="text-white mb-3"
      style={{
        background: "linear-gradient(135deg, var(--btn-bg) 0%, var(--btn-hover-bg) 100%)",
        borderRadius: 16, padding: "20px 24px", boxShadow: "0 8px 24px rgba(15, 23, 42, 0.12)", position: "relative", overflow: "hidden",
      }}
    >
      <i className="bi bi-airplane-fill" style={{ position: "absolute", right: -10, bottom: -30, fontSize: 150, opacity: 0.07, transform: "rotate(-20deg)" }}></i>
      <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-3" style={{ position: "relative" }}>
        <div className="d-flex align-items-center gap-2 flex-wrap">{chips}</div>
        <div className="d-flex align-items-center gap-2 flex-wrap">{right}</div>
      </div>
      <div className="d-flex align-items-center justify-content-between gap-3" style={{ position: "relative" }}>
        {side(from, "left")}
        <div className="flex-grow-1 text-center px-2" style={{ maxWidth: 420 }}>
          <div className="d-flex align-items-center gap-2">
            <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#fff" }}></span>
            <span className="flex-grow-1" style={{ borderTop: "2px dashed rgba(255,255,255,.55)" }}></span>
            <i className="bi bi-airplane-fill" style={{ fontSize: 18, transform: "rotate(90deg)" }}></i>
            <span className="flex-grow-1" style={{ borderTop: "2px dashed rgba(255,255,255,.55)" }}></span>
            <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#fff" }}></span>
          </div>
          {middle && <div style={{ fontSize: 12, opacity: 0.85, marginTop: 6 }}>{middle}</div>}
        </div>
        {side(to, "right")}
      </div>
    </div>
  );
};

// Compact label/value list for one category (rows: [{ label, value, badge?, hideIfEmpty? }])
export const DetailGroup = ({ icon, title, accent, rows }) => (
  <div className="h-100" style={{ border: "1px solid #eef1f7", borderRadius: 10, overflow: "hidden", background: "#fff" }}>
    <div
      className="d-flex align-items-center gap-2"
      style={{ padding: "8px 12px", background: accent + "12", color: "#0f172a", fontWeight: 700, fontSize: 13 }}
    >
      <i className={"bi " + icon} style={{ color: accent }}></i>
      {title}
    </div>
    <div style={{ padding: "4px 12px 6px" }}>
      {rows
        .filter((r) => !r.hideIfEmpty || !isEmpty(r.value))
        .map((r) => (
          <div
            key={r.label}
            className="d-flex justify-content-between align-items-start gap-3"
            style={{ padding: "6px 0", borderBottom: "1px dashed #eef1f7", fontSize: 13 }}
          >
            <span style={{ ...detailLabelStyle, paddingTop: 2, flexShrink: 0 }}>{r.label}</span>
            <span className="fw-semibold text-end" style={{ color: r.color || "#1e293b", overflowWrap: "anywhere" }}>
              {r.badge ? <Badge value={r.value} /> : show(r.value)}
            </span>
          </div>
        ))}
    </div>
  </div>
);

// Titled card for a table / free-form section, with an optional count badge
export const DetailBlock = ({ icon, title, count, accent, children }) => (
  <div
    className="mb-3"
    style={{ background: "#fff", border: "1px solid #eef1f7", borderRadius: 12, boxShadow: "0 1px 3px rgba(15, 23, 42, 0.04)", overflow: "hidden" }}
  >
    <div
      className="d-flex align-items-center gap-2"
      style={{ padding: "9px 14px", borderBottom: "1px solid #eef1f7", background: accent + "12", fontWeight: 700, fontSize: 14, color: "#0f172a" }}
    >
      <i className={"bi " + icon} style={{ color: accent }}></i>
      {title}
      {count !== undefined && (
        <span style={{ background: accent + "26", color: accent, borderRadius: 999, padding: "0 8px", fontSize: 11, fontWeight: 700 }}>{count}</span>
      )}
    </div>
    <div style={{ padding: 10 }}>{children}</div>
  </div>
);

// One slim bar of key figures instead of separate stat cards (stats: [{ icon, label, value, accent }])
export const StatStrip = ({ stats }) => (
  <div
    className="d-flex flex-wrap mb-3"
    style={{ background: "#fff", border: "1px solid #eef1f7", borderRadius: 12, boxShadow: "0 1px 3px rgba(15, 23, 42, 0.04)" }}
  >
    {stats.map((s, i) => (
      <div
        key={s.label}
        className="d-flex align-items-center gap-2"
        style={{ flex: "1 1 140px", padding: "10px 16px", borderLeft: i ? "1px solid #eef1f7" : "none" }}
      >
        <i className={"bi " + s.icon} style={{ color: s.accent, fontSize: 18 }}></i>
        <div>
          <div style={detailLabelStyle}>{s.label}</div>
          <div className="fw-bold" style={{ fontSize: 17, color: "#0f172a", lineHeight: 1.15 }}>{s.value}</div>
        </div>
      </div>
    ))}
  </div>
);
